import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  FaServer, 
  FaDatabase, 
  FaCloud, 
  FaCode,
  FaShieldAlt,
  FaLaptopCode
} from "react-icons/fa";
import {
  headingAnimation,
  sectionBodyAnimation,
} from "../../../hooks/useAnimation";
import { BottomLine } from "../../../components";

const Service = () => {
  const [ref, inView] = useInView({
    triggerOnce: true, // Changed to true for better performance
    threshold: 0.1, // Reduced threshold
  });
  const [statsRef, statsInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const [viewDiv, setViewDiv] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const statsData = [
    { number: "50+", label: "Projects" },
    { number: "50+", label: "APIs" },
    { number: "50+", label: "Databases" },
    { number: "100%", label: "Satisfaction" },
  ];

  useEffect(() => {
    setViewDiv(inView);
    
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [inView]);

  const services = [
    {
      id: 1,
      title: "Full Stack Development",
      icon: <FaLaptopCode className="text-4xl" />,
      description: "End-to-end web application development using MERN stack with scalable architecture.",
      tech: ["React", "Node.js", "Express", "MongoDB"]
    },
    {
      id: 2,
      title: "Backend & API Development",
      icon: <FaServer className="text-4xl" />,
      description: "Building robust RESTful & GraphQL APIs, authentication systems, and database management.",
      tech: ["Node.js", "Express", "JWT", "REST/GraphQL"]
    },
    {
      id: 3,
      title: "Database Design & Optimization",
      icon: <FaDatabase className="text-4xl" />,
      description: "Designing efficient database schemas and implementing both SQL & NoSQL databases.",
      tech: ["MongoDB", "PostgreSQL", "Redis", "Mongoose"]
    },
    {
      id: 4,
      title: "Frontend Architecture",
      icon: <FaCode className="text-4xl" />,
      description: "Creating responsive, interactive UIs with modern React and performance optimization.",
      tech: ["React", "Redux", "Tailwind", "Next.js"]
    },
    {
      id: 5,
      title: "Cloud & DevOps",
      icon: <FaCloud className="text-4xl" />,
      description: "Deployment, CI/CD pipelines, containerization with Docker, and cloud services.",
      tech: ["AWS", "Docker", "GitHub Actions", "Nginx"]
    },
    {
      id: 6,
      title: "Performance & Security",
      icon: <FaShieldAlt className="text-4xl" />,
      description: "Implementing security best practices, performance optimization, and code quality.",
      tech: ["JWT", "Helmet.js", "Lighthouse", "Testing"]
    }
  ];

  // FASTER animation variants - similar to Projects
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.5, // Reduced from 0.6
        ease: "easeOut" // Changed from spring to easeOut
      }
    },
  };

  return (
    <div className="py-20 bg-gradient-to-b from-[#08060f] to-[#0c0a16] text-white relative overflow-hidden" id="services">
      {/* Animated Background Elements - Lighter for mobile */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 left-10 w-48 h-48 md:w-72 md:h-72 bg-violet-700/10 rounded-full blur-2xl md:blur-3xl"></div>
        <div className="absolute bottom-1/4 right-10 w-64 h-64 md:w-96 md:h-96 bg-indigo-800/10 rounded-full blur-2xl md:blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
      <motion.div
        className="mb-12 relative z-10"
        initial="hidden"
        animate={viewDiv && "visible"}
        variants={headingAnimation}
      >
        
        <h1 className="text-4xl font-semibold text-center mb-3">
          My <span className="text-violet-400">Services</span>
        </h1>
        <h3 className="text-center text-gray-400 text-lg tracking-wide">
          What I Offer
        </h3>
        <BottomLine />
      </motion.div>

      {/* Service Cards - FASTER ANIMATION */}
      <motion.div
        ref={ref}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 relative z-10"
        initial="hidden"
        animate={viewDiv ? "visible" : "hidden"}
        variants={sectionBodyAnimation}
      >
        {services.map((service, i) => (
          <motion.div
            key={service.id}
            initial="hidden"
            animate={viewDiv ? "visible" : "hidden"}
            variants={cardVariants}
            transition={{ 
              delay: isMobile ? 0 : i * 0.1, // Less delay on mobile
              duration: 0.4 // Faster
            }}
            whileHover={{
              y: -8,
              scale: 1.02,
              boxShadow: "0px 18px 38px rgba(124, 58, 237, 0.14)",
            }}
            className="p-6 md:p-8 rounded-2xl bg-gradient-to-br from-violet-900/20 via-gray-900/20 to-indigo-900/10 
                       backdrop-blur-md border border-violet-500/20 shadow-lg 
                       hover:shadow-violet-500/20 cursor-pointer flex flex-col h-full
                       group relative overflow-hidden transition-all duration-300"
          >
            {/* Glow Effect - Lighter */}
            <div className="absolute inset-0 bg-gradient-to-r from-violet-600/0 via-violet-600/5 to-indigo-600/0 
                          opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            {/* Icon Container */}
            <div className="relative z-10 mb-4 md:mb-6">
              <div className="inline-flex items-center justify-center p-3 md:p-4 rounded-2xl 
                            bg-gradient-to-br from-violet-600/30 to-indigo-600/30 
                            border border-violet-500/30 mb-3 md:mb-4">
                <div className="text-white">
                  {service.icon}
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="relative z-10 flex-grow">
              <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-white">
                {service.title}
              </h2>
              <p className="text-gray-300 leading-relaxed text-sm md:text-base mb-4 md:mb-6">
                {service.description}
              </p>
              
              {/* Tech Stack */}
              <div className="mt-auto">
                <div className="flex flex-wrap gap-1 md:gap-2">
                  {service.tech.map((tech, idx) => (
                    <span 
                      key={idx}
                      className="px-2 py-1 text-xs bg-violet-900/40 text-violet-200 
                               rounded-full border border-violet-700/30"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Hover Indicator */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-indigo-500 
                          transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
          </motion.div>
        ))}
      </motion.div>

      {/* Stats Section - Scroll par aate hi 70+, 50+, 50+, 100% dikhenge (count-up nahi) */}
      <motion.div
        ref={statsRef}
        initial={{ opacity: 0, y: 30 }}
        animate={statsInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="mt-12 md:mt-16 relative z-10"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {statsData.map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={statsInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.3 + (idx * 0.05), duration: 0.3 }}
              whileHover={{ scale: 1.03 }}
              className="text-center p-4 md:p-6 rounded-xl bg-gradient-to-br from-violet-900/20 to-indigo-900/20
                         border border-violet-500/10 backdrop-blur-sm"
            >
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-1 md:mb-2">
                {stat.number}
              </h3>
              <p className="text-gray-400 text-xs md:text-sm">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
    </div>
  );
};

export default Service;